/* eslint-disable no-extend-native */
import {ResizeObserver} from '@juggle/resize-observer';
import 'setimmediate';
import platform from 'platform';
// eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
// @ts-ignore (PNPM)
window.platform = platform;

if (window.ResizeObserver == null) {
  window.ResizeObserver = ResizeObserver;
}

if (typeof globalThis !== 'object') {
  // @ts-expect-error This is a polyfill for older browsers that don't have `globalThis`
  window.globalThis = window;
}

/**
 * Define toJSON for ES6 data structures to assist serialization.
 *
 * const m = new Map().set('a', 1).set('b', 2);
 *
 * // Without toJSON
 * JSON.stringify(m) === '{}'
 * new Map(JSON.parse(JSON.stringify(m))); // crashes
 *
 * // With toJSON
 * JSON.stringify(m) === '[["a",1],["b",2]]'
 * new Map(JSON.parse(JSON.stringify(m))); // works
 */
(Map.prototype as any).toJSON = function () {
  return Array.from(this);
};

/**
 * const s = new Set([1,2]);
 *
 * // Without toJSON
 * JSON.stringify(s) === '{}'
 * new Set(JSON.parse(JSON.stringify(s))); // crashes
 *
 * // With toJSON
 * JSON.stringify(s) === '[1,2]'
 * new Set(JSON.parse(JSON.stringify(s))); // works
 */
(Set.prototype as any).toJSON = function () {
  return Array.from(this);
};

/* eslint-disable one-var, prefer-const */
// Polyfill from: https://github.com/jarek-foksa/geometry-polyfill
//
// MIT License
//
// Copyright (c) 2018 Jarek Foksa
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.
// @info
//   DOMPoint polyfill
// @src
//   https://drafts.fxtf.org/geometry/#DOMPoint
//   https://github.com/chromium/chromium/blob/master/third_party/blink/renderer/core/geometry/dom_point_read_only.cc
{
  class DOMPoint {
    x: number;
    y: number;
    z: number;
    w: number;

    constructor(x = 0, y = 0, z = 0, w = 1) {
      this.x = x;
      this.y = y;
      this.z = z;
      this.w = w;
    }

    static fromPoint(otherPoint: DOMPoint) {
      return new DOMPoint(
        otherPoint.x,
        otherPoint.y,
        otherPoint.z !== undefined ? otherPoint.z : 0,
        otherPoint.w !== undefined ? otherPoint.w : 1,
      );
    }

    matrixTransform(matrix: DOMMatrix) {
      if ((matrix.is2D || matrix instanceof SVGMatrix) && this.z === 0 && this.w === 1) {
        return new DOMPoint(
          this.x * matrix.a + this.y * matrix.c + matrix.e,
          this.x * matrix.b + this.y * matrix.d + matrix.f,
          0,
          1,
        );
      } else {
        return new DOMPoint(
          this.x * matrix.m11 + this.y * matrix.m21 + this.z * matrix.m31 + this.w * matrix.m41,
          this.x * matrix.m12 + this.y * matrix.m22 + this.z * matrix.m32 + this.w * matrix.m42,
          this.x * matrix.m13 + this.y * matrix.m23 + this.z * matrix.m33 + this.w * matrix.m43,
          this.x * matrix.m14 + this.y * matrix.m24 + this.z * matrix.m34 + this.w * matrix.m44,
        );
      }
    }

    toJSON() {
      return {
        x: this.x,
        y: this.y,
        z: this.z,
        w: this.w,
      };
    }
  }

  if (window.DOMPoint == null) {
    window.DOMPoint = DOMPoint;
  }
}

// @info
//   DOMRect polyfill
// @src
//   https://drafts.fxtf.org/geometry/#DOMRect
//   https://github.com/chromium/chromium/blob/master/third_party/blink/renderer/core/geometry/dom_rect_read_only.cc
{
  class DOMRect {
    x: number;
    y: number;
    width: number;
    height: number;

    constructor(x = 0, y = 0, width = 0, height = 0) {
      this.x = x;
      this.y = y;
      this.width = width;
      this.height = height;
    }

    static fromRect(otherRect: DOMRect) {
      return new DOMRect(otherRect.x, otherRect.y, otherRect.width, otherRect.height);
    }

    get top() {
      return this.y;
    }

    get left() {
      return this.x;
    }

    get right() {
      return this.x + this.width;
    }

    get bottom() {
      return this.y + this.height;
    }

    toJSON() {
      return {
        x: this.x,
        y: this.y,
        width: this.width,
        height: this.height,
        top: this.top,
        left: this.left,
        right: this.right,
        bottom: this.bottom,
      };
    }
  }

  if (window.DOMRect == null) {
    window.DOMRect = DOMRect;
  }
}

if (window.TextEncoder == null) {
  require('fast-text-encoding');
}
